import { useEffect, useState } from 'react';
import { createSpending, apiResultSuccess } from '../apihelper';
import { FORM_MESSAGES, LABELS, UTILITY_CSS_CLASSES } from '../global/constants';
import { makeBEM, fadeOutThenExecute, fadeOutFormMsg, getDefaultFormMsg, timeStampToDate, gracefulFinally, getCurrentMonthDateRange } from '../global/utils';
import Icon from "../atoms/Icon";
import FormStatus from '../atoms/FormStatus';
import FormButton from '../atoms/FormButton';
import { ConfirmDialog } from '../components/Dialogs';

function SpendingForm({ moduleName='spending-form', 
						boardId=-1,
						controller,
						log,
						categories=[],
						newLogSuccess,
						discardForm,
						editSuccess,
						deleteSuccess,
						initDateRange=false
					}) {

	const defaultCatIcon = 'budget';
	const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [formData, setFormData] = useState({ 
												amount: 0, 
												description: '', 
												date_created: timeStampToDate(new Date()),
												category_id: 0,
											});

	const [errors, setErrors] = useState({});
	const [formStatus, setFormStatus] = useState( FORM_MESSAGES.spendingFormInstructions );
    const [editMode, setEditMode] = useState(false);
    const [fadeOutTimer, setFadeOutTimer] = useState(false);
	const [ctrl, setCtrl] = useState(false);
	const [catOptions, setCatOptions] = useState([]);
	const [catIcon, setCatIcon] = useState(defaultCatIcon);
	const [dateRange, setDateRange] = useState( getCurrentMonthDateRange() )
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);

	const formWrapperClass = `form ${moduleName}`;
	const inputsWrapperClass = `${makeBEM('form', 'inputs-wrapper')} ${makeBEM(moduleName, 'inputs-wrapper')}`;

	const amountErrorClass = errors.amount ? UTILITY_CSS_CLASSES.hasErrors : '';
	const amountInputClass = `input ${makeBEM(moduleName, 'amount-input')} ${amountErrorClass}`

	const descriptionErrorClass = errors.description ? UTILITY_CSS_CLASSES.hasErrors : '';
	const descriptionInputClass = `input ${makeBEM(moduleName, 'description-input')} ${descriptionErrorClass}`;
	
	const catErrorClass = errors.category ? UTILITY_CSS_CLASSES.hasErrors : '';
	const catSelectClass = `select ${makeBEM(moduleName, 'category-select')} ${catErrorClass}`;
	const catIconClass = `${makeBEM(moduleName, 'category-icon')}`;

	const showDeleteBtn = editMode;
	const btnsDisabled = isSubmitting || isDeleting;

	useEffect(() => {
		if ( log ) {
			setFormData(log);
			setEditMode(true);
		}
	}, [log]);

	useEffect(() => {
		if ( categories ) {
			buildCategoryOptions();
		}
	}, [categories]);

	useEffect(() => {
		if ( controller ) {
			setCtrl(controller);
		}
	}, [controller]);

	useEffect(() => {
		if ( initDateRange ) {
			setDateRange(initDateRange);
		}
	}, [dateRange])

	const handleChange = (e) => {
		const { name, value } = e.target;
		const updatedFormData = { ...formData, [name]: value };

		if ( name === 'category_id' ) {
			const parsedValue = parseInt(value);
			updatedFormData.category_id = isNaN(parsedValue) ? value : parsedValue;
			changeCatIcon(parsedValue);
		}

		setFormData(updatedFormData);
	}

	const changeCatIcon = (catId=-1) => {
		if ( catId === -1 || catOptions.length === 0 ) {
			setCatIcon( defaultCatIcon );
		}

		const cat = categories.get(catId);
		const catIcon = cat.getIconName(); 	
		setCatIcon( catIcon );
	}

	const validateForm = () => {
		const newErrors = {};

		if ( !formData.amount ) {
			newErrors.amount = FORM_MESSAGES.amountRequired;
		} 

		if ( !formData.description ) {
			newErrors.description = FORM_MESSAGES.descriptionRequired;
		}

		if ( !formData.category_id || formData.category_id === -1 ) {
			newErrors.category = FORM_MESSAGES.categoryRequired;
		}

		return newErrors;
	}
	
	const handleSubmit = (e) => {
		e.preventDefault();
		const validationErrors = validateForm();

		if ( Object.keys(validationErrors).length > 0 ) {
			setErrors(validationErrors);
		} else {
			setErrors({});
			const postData = { ...formData, board_id: boardId, ...dateRange };

			if ( !editMode ) {
				handleSubmitNew(postData);
			} else {
				handleSubmitEdit(postData);
			}
		}
	}

	const handleSubmitNew = (postData) => {

		setIsSubmitting(true);
		setFormStatus(FORM_MESSAGES.newSpendingSubmit);
		
		if ( ctrl.getTallyId ) {
			postData.tally_id = ctrl.getTallyId();
		}

		try {
			createSpending(
				(result) => {
	
					if ( apiResultSuccess(result) ) {

						setFormStatus(FORM_MESSAGES.newSpendingSuccess);

						if ( ctrl ) {
							ctrl.newLogSuccess(result);
						}

						setFadeOutTimer(
							fadeOutThenExecute(discardForm)
						);
					} else {
                        setFormStatus(FORM_MESSAGES.failedSubmit);
                    }
				}, postData
			);
		} catch (error) {
			setFormStatus(FORM_MESSAGES.failedSubmit);
		} finally {
			gracefulFinally(setIsSubmitting);
		}
	}

	const handleSubmitEdit = (postData) => {
		setIsSubmitting(true);
		// 1. Update formStatus
		// 2. API call
		// 3. handle api response
	}


	const getFormStatus = () => {
		for ( var prop in errors ) {
			return errors[prop];
		}

		if ( errors.amount ) {
			return errors.amount
		}
		
		if ( errors.description ) {
			return errors.description
		}

		if ( formStatus ) {
            return formStatus;
        }

		return getDefaultFormMsg(editMode, FORM_MESSAGES.spendingEdit, FORM_MESSAGES.spendingFormInstructions);
	}

	const resetForm = () => {
        setFormData({ amount: 0, description: ''});
        setErrors({});
    }

    const handleDiscard = () => {
        resetForm();
        discardForm();

        if ( fadeOutTimer ) {
            clearTimeout(fadeOutTimer);
        }
    }

	const handleDelete = () => {
		discardConfirmDialog();
		// const postData = { ...formData, id: formData.id };
		setIsDeleting(true);

		try {
			// call delete api 
			// deleteToDo(
			// 	(result) => {
			// 		if ( apiResultSuccess(result) ) {
			// 			setFormStatus(FORM_MESSAGES.deletedToDo);
			// 			if ( deleteSuccess ) {
			// 				setFadeOutTimer(
			// 					fadeOutThenExecute(() => {
			// 						deleteSuccess(postData.todo_id);
			// 					})
			// 				);
			// 			}
			// 		} else {
			// 			setFormStatus(message: result.message);
			// 		}
			// 	},
			// 	postData
			// );
		} catch {
			setFormStatus(FORM_MESSAGES.failedSubmit);
		} finally {
			gracefulFinally(setIsDeleting);
		}
	}

	const buildCategoryOptions = () => {
		const catMap = new Map();
		
		catMap.set(-1, LABELS.category);

		categories.forEach((cat, index) => {
			catMap.set(cat.id, cat.name)
		});

		setCatOptions(catMap);
	}

    const promptDeleteConfirm = () => {
        setShowConfirmDialog(true);
    }

    const discardConfirmDialog = () => {
        setShowConfirmDialog(false);
    }

	const amountValue = log ? { value: formData.amount } : '';

	return (
		<div className={ formWrapperClass }>
			<div className={ makeBEM(moduleName, 'category-icon-wrapper') } >
				<div className={ makeBEM(moduleName, 'category-icon-cropper') } >
					<Icon customClass={ catIconClass } size={6} name={catIcon} type="fill" />
				</div>
			</div>
			<form onSubmit={handleSubmit}>
				<div className={ inputsWrapperClass }>
					<FormStatus
						errors={errors}
						formStatus= { getFormStatus() }
						editMode={editMode}
						customClass={ makeBEM(moduleName, 'form-status') }
					/>
					<input
						id="amount"
						name="amount"
						type="number"
						step="0.01"
						className={ amountInputClass }
						onChange={ handleChange }
						autoComplete="off"
						required
						placeholder={ LABELS.spendingAmountPlaceholder }
						autoFocus
						{...amountValue }
					/>
					<input
                        id="description"
                        name="description"
                        type="text"
                        className={ descriptionInputClass }
                        onChange={ handleChange }
                        autoComplete="off"
                        required
                        placeholder={ LABELS.descriptionPlaceholder }
                        value={ formData.description }
                    />

					<div className={ `${makeBEM('form', 'category-select-wrapper')} ${makeBEM(moduleName, 'date-input-wrapper')}` }>
						<label
							className={ `label ${makeBEM(moduleName, 'category-label')}` } 
							htmlFor="category_id">{LABELS.category}
						</label>
					
						<select
							className={ catSelectClass }
							id="category_id"
							name="category_id"
							onChange = { handleChange }
							value={ formData.category_id }
						>

							{
								Array.from(catOptions.values()).map((value, index) => (
								<option key={index} value={ parseInt(index) } disabled={ index === 0 }>
										{value}
									</option>
								))
							}
						</select>
					</div>

					<div className={ `${makeBEM('form', 'date-input-wrapper')} ${makeBEM(moduleName, 'date-input-wrapper')}` }>
						<label
							className={ `label ${makeBEM(moduleName, 'date-created-label')}` } 
							htmlFor="date_created">{LABELS.spendingDate}
						</label>
						<input
							id="date_created"
							name="date_created"
							type="date"
							className={ `input ${makeBEM(moduleName, 'date-created')}` } 
							onChange={ handleChange }
							value={ formData.date_created ? formData.date_created : '' } 
							min={dateRange.date_start}
							max={dateRange.date_end}

						/>
					</div>

					<div className={ `${makeBEM('form', 'btns-wrapper')} ${makeBEM(moduleName, 'btns-wrapper')}` }>
						<FormButton
							customClass={ makeBEM(moduleName, 'submit-btn') }
							type="submit"
							onClick={handleSubmit}
							disabled={btnsDisabled}
							isSubmitting={isSubmitting}
						/>
						<FormButton
							customClass={ makeBEM(moduleName, 'discard-btn') }
							onClick={handleDiscard}
							disabled={btnsDisabled}
							type="button"
							icon="close"
						/>
						{
							showDeleteBtn &&
							<FormButton
								customClass={ makeBEM(moduleName, 'delete-btn') }
								onClick={promptDeleteConfirm}
								disabled={btnsDisabled}
								isSubmitting={isDeleting}
								type="button"
								icon="delete"
							/>
						}
					</div>
				</div>
			</form>

			{
                showConfirmDialog &&
                <ConfirmDialog
                    show={showConfirmDialog}
                    message={LABELS.confirmDelete}
                    confirmAction={handleDelete}
                    discardAction={discardConfirmDialog}
                />
            }
		</div>
	);
}

export default SpendingForm;